import axios from 'axios';
import { API_BASE_URL, API_TOKEN_KEY, ADMIN_DATA_KEY } from './config/api';


const proxy = "https://cors-anywhere.herokuapp.com/";

export const getToken = () => localStorage.getItem(API_TOKEN_KEY);

export const getUserData = () => {
  try {
    const raw = localStorage.getItem(ADMIN_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Admin list/filter endpoints mapping (former GET -> POST)
const GET_TO_POST_LIST_ENDPOINTS = [
  // Users
  { pattern: /^\/admin\/users$/, to: () => '/admin/users' },
  { pattern: /^\/admin\/users\/([^/]+)\/ledger$/, to: (m) => `/admin/users/${m[1]}/ledger` },
  { pattern: /^\/admin\/users\/([^/]+)\/transactions$/, to: (m) => `/admin/users/${m[1]}/transactions` },
  // Dashboard
  { pattern: /^\/admin\/dashboard\/?$/, to: () => '/admin/dashboard' },
  // Transactions
  { pattern: /^\/admin\/transactions$/, to: () => '/admin/transactions' },
  // Ledger
  { pattern: /^\/admin\/ledger$/, to: () => '/admin/ledger' },
  { pattern: /^\/admin\/ledger\/([^/]+)$/, to: (m) => `/admin/ledger/${m[1]}` },
  // Payouts
  { pattern: /^\/admin\/payouts$/, to: () => '/admin/payouts' },
  // Bank statement
  { pattern: /^\/admin\/bank-statement$/, to: () => '/admin/bank-statement/list' },
  { pattern: /^\/admin\/bank-statement\/balance$/, to: () => '/admin/bank-statement/balance' },
  // Physical redemptions
  { pattern: /^\/admin\/physical-redemptions$/, to: () => '/admin/physical-redemptions' },
  { pattern: /^\/admin\/physical-redemptions\/lookup$/, to: () => '/admin/physical-redemptions/lookup' },
  // Reconciliation
  { pattern: /^\/admin\/reconciliation\/runs$/, to: () => '/admin/reconciliation/runs' },
  { pattern: /^\/admin\/reconciliation\/exceptions$/, to: () => '/admin/reconciliation/exceptions' },
  // Investments
  { pattern: /^\/admin\/investments$/, to: () => '/admin/investments' },
  // Rewards
  { pattern: /^\/admin\/rewards\/rules$/, to: () => '/admin/rewards/rules/list' },
  { pattern: /^\/admin\/rewards\/redeemable$/, to: () => '/admin/rewards/redeemable' },
  { pattern: /^\/admin\/rewards\/redeemable\/users\/([^/]+)\/grants$/, to: (m) => `/admin/rewards/redeemable/users/${m[1]}/grants` },
  { pattern: /^\/admin\/rewards\/usage$/, to: () => '/admin/rewards/usage' },
  { pattern: /^\/admin\/rewards\/usage\/users\/([^/]+)\/transactions$/, to: (m) => `/admin/rewards/usage/users/${m[1]}/transactions` },
  // Audit
  { pattern: /^\/admin\/audit-logs$/, to: () => '/admin/audit-logs' },
  // Support
  { pattern: /^\/admin\/support\/tickets$/, to: () => '/admin/support/tickets' },
  { pattern: /^\/admin\/support\/tickets\/([^/]+)\/messages$/, to: (m) => `/admin/support/tickets/${m[1]}/messages/list` },
  // Notifications
  { pattern: /^\/admin\/notifications\/campaigns$/, to: () => '/admin/notifications/campaigns/list' },
  { pattern: /^\/admin\/notifications\/campaigns\/([^/]+)\/recipients$/, to: (m) => `/admin/notifications/campaigns/${m[1]}/recipients` },
  { pattern: /^\/admin\/notifications\/inbox$/, to: () => '/admin/notifications/inbox' },
  // Devices
  { pattern: /^\/admin\/devices\/push-status$/, to: () => '/admin/devices/push-status' },
  // CMS
  { pattern: /^\/admin\/cms\/blogs$/, to: () => '/admin/cms/blogs/list' },
  { pattern: /^\/admin\/cms\/faqs$/, to: () => '/admin/cms/faqs/list' },
  { pattern: /^\/admin\/cms\/legal$/, to: () => '/admin/cms/legal/list' },
  { pattern: /^\/admin\/cms\/content-types$/, to: () => '/admin/cms/content-types/list' },
  { pattern: /^\/admin\/cms\/videos$/, to: () => '/admin/cms/videos/list' },
  // Offers
  { pattern: /^\/admin\/offers$/, to: () => '/admin/offers/list' },
];

const POST_LIST_ENDPOINTS = [
  /^\/admin\/users$/,
  /^\/admin\/users\/[^/]+\/ledger$/,
  /^\/admin\/users\/[^/]+\/transactions$/,
  /^\/admin\/dashboard\/?$/,
  /^\/admin\/transactions$/,
  /^\/admin\/ledger$/,
  /^\/admin\/ledger\/[^/]+$/,
  /^\/admin\/payouts$/,
  /^\/admin\/bank-statement\/list$/,
  /^\/admin\/bank-statement\/balance$/,
  /^\/admin\/physical-redemptions$/,
  /^\/admin\/physical-redemptions\/lookup$/,
  /^\/admin\/reconciliation\/runs$/,
  /^\/admin\/reconciliation\/exceptions$/,
  /^\/admin\/investments$/,
  /^\/admin\/rewards\/rules\/list$/,
  /^\/admin\/rewards\/redeemable$/,
  /^\/admin\/rewards\/redeemable\/users\/[^/]+\/grants$/,
  /^\/admin\/rewards\/usage$/,
  /^\/admin\/rewards\/usage\/users\/[^/]+\/transactions$/,
  /^\/admin\/audit-logs$/,
  /^\/admin\/support\/tickets$/,
  /^\/admin\/support\/tickets\/[^/]+\/messages\/list$/,
  /^\/admin\/notifications\/campaigns\/list$/,
  /^\/admin\/notifications\/campaigns\/[^/]+\/recipients$/,
  /^\/admin\/notifications\/inbox$/,
  /^\/admin\/devices\/push-status$/,
  /^\/admin\/cms\/blogs\/list$/,
  /^\/admin\/cms\/faqs\/list$/,
  /^\/admin\/cms\/legal\/list$/,
  /^\/admin\/cms\/content-types\/list$/,
  /^\/admin\/cms\/videos\/list$/,
  /^\/admin\/offers\/list$/,
  /^\/admin\/referrals\/users$/,
];

apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    const userData = getUserData();

    const cleanUrl = typeof config.url === 'string' ? config.url.split('?')[0] : '';
    const method = (config.method || 'get').toLowerCase();

    // Transform legacy GET list endpoints to POST with body
    if (method === 'get') {
      const match = GET_TO_POST_LIST_ENDPOINTS.find((e) => e.pattern.test(cleanUrl));
      if (match) {
        config.method = 'post';
        config.url = match.to(cleanUrl.match(match.pattern));
        config.data = config.data || config.params || {};
        delete config.params;
        config.skipBodyWrap = true;
      }
    }

    // Check if this is an admin list/filter endpoint (now POST)
    const effectiveUrl = typeof config.url === 'string' ? config.url.split('?')[0] : '';
    const isPostListEndpoint =
      (config.method || '').toLowerCase() === 'post' &&
      POST_LIST_ENDPOINTS.some((pattern) => pattern.test(effectiveUrl));

    if (isPostListEndpoint) {
      config.skipBodyWrap = true;
      if (config.data === undefined || config.data === null) {
        config.data = {};
      }
      if (config.params) {
        config.data = { ...config.params, ...config.data };
        delete config.params;
      }
    }

    // Auth endpoints: send JSON body as-is, no Authorization
    const isAuthEndpoint =
      typeof config.url === 'string' &&
      (config.url.includes('/admin/auth/login') || config.url.includes('/admin/auth/verify-otp'));
    if (isAuthEndpoint) {
      return config;
    }

    // Ensure headers exist and send token for all other requests
    config.headers = {
      ...(config.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    // Always ensure Content-Type: application/json for non-FormData requests
    if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    // REST endpoints that expect a plain JSON body (not the legacy wrapped shape)
    if (config.skipBodyWrap) {
      return config;
    }

    // GET and DELETE requests must not have a body — skip wrapping
    if (!config.data || config.method === 'get' || config.method === 'delete') {
      return config;
    }

    // Handle FormData differently
    if (config.data instanceof FormData) {
      const formFields = {};
      const fileEntries = [];

      for (const [key, value] of config.data.entries()) {
        if (key.startsWith('image_') || key === 'image' || key === 'pdf') {
          fileEntries.push({ key, file: value });
        } else {
          try {
            const parsed = JSON.parse(value);
            formFields[key] = parsed;
          } catch {
            formFields[key] = value;
          }
        }
      }

      const requestData = {
        authentication: `Bearer ${token || ''}`,
        input: {
          ...formFields,
          by_user: userData?.id || '',
        },
      };

      const newFormData = new FormData();
      // FormData values must be strings or Blobs – stringify the payload
      newFormData.append('data', JSON.stringify(requestData));

      fileEntries.forEach(({ key, file }) => {
        newFormData.append(key, file);
      });

      config.data = newFormData;
    } else {
      // Regular JSON body
      const requestData = {
        authentication: `Bearer ${token || ''}`,
        input: {
          ...(config.data || {}),
          by_user: userData?.id || '',
        },
      };
      config.data = { data: requestData };
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/** Dispatch event so the app can show "Token expired" modal; no redirect until user clicks "Go to login". */
function notifyTokenExpired() {
  window.dispatchEvent(new CustomEvent('admin:token-expired'));
}

apiClient.interceptors.response.use(
  (response) => {
    const data = response?.data;
    if (data && data.token_expired === true) {
      notifyTokenExpired();
      return Promise.reject(new Error('Token expired'));
    }
    return response;
  },
  (error) => {
    const data = error?.response?.data;
    if (data && data.token_expired === true) {
      notifyTokenExpired();
      return Promise.reject(new Error('Token expired'));
    }
    return Promise.reject(error);
  },
);
